'use client';
import React from 'react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import * as rModal from '@radix-ui/react-dialog';
import cn from 'classnames';
import styles from './modal.module.sass';
import { FontIcon, Spinner, Text } from '@intigral/shared';
import withPresetWidth, { widthPresets } from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import withResponsiveStyles, {
    ResponsiveValue,
} from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';

export interface ModalProps {
    children: React.ReactNode;
    open?: boolean;
    onOpenChange: (open?: boolean) => void;
    closePlacement?: 'start' | 'end' | 'null' | null;
    closeSize?: 'small';
    theme?: 'black';
    widthSet?: keyof typeof widthPresets;
    back?: boolean;
    alignSelf?: 'bottom';
    className?: string;
    padding?: ResponsiveValue;
    title?: string;
    isLoading?: boolean;
    /** Optional. When set (e.g. fullscreen element), portal renders into it instead of body. Used for No Ads CTA in fullscreen. */
    container?: HTMLElement;
}

const OverlayStyle = withResponsiveStyles(rModal.Content);
const OverlayWidth = withPresetWidth(OverlayStyle);

export const Modal = ({
    children,
    open,
    onOpenChange,
    closePlacement = 'end',
    closeSize,
    theme,
    widthSet,
    back,
    alignSelf = 'bottom',
    padding,
    title,
    className,
    isLoading,
    container,
}: ModalProps) => {
    const hideClose = closePlacement === null || closePlacement === 'null';

    return (
        <rModal.Root open={open} onOpenChange={onOpenChange}>
            <rModal.Portal {...(container && { container })}>
                <rModal.Overlay className={cn(styles.overlay, { [styles[`align-${alignSelf}`]]: alignSelf })}>
                    <OverlayWidth
                        className={cn(styles.content, { [styles[`theme-${theme}`]]: theme }, className)}
                        presetWidth={widthSet}
                        padding={padding}
                        aria-describedby={undefined}
                    >
                        {/* https://www.radix-ui.com/primitives/docs/components/dialog#title */}
                        <VisuallyHidden>
                            <rModal.Title className="DialogTitle">{title ?? 'Modal'}</rModal.Title>
                        </VisuallyHidden>

                        {isLoading ? (
                            <Spinner block style={{ minHeight: '80vh' }} />
                        ) : (
                            <>
                                {!hideClose && (
                                    <rModal.Close
                                        className={cn(
                                            styles.close,
                                            styles[`close-${closePlacement}`],
                                            closeSize && styles[`${closeSize}`]
                                        )}
                                        data-test-id="modal-close-btn"
                                    >
                                        {back ? (
                                            <FontIcon name="left-arrow" size="smaller" md="small" />
                                        ) : (
                                            <FontIcon name="close" size="smaller" md="small" />
                                        )}
                                    </rModal.Close>
                                )}

                                {title && <strong className={styles.title}>{title}</strong>}

                                {children && (
                                    <div className={cn(styles.children, hideClose && styles.noclose)}>{children}</div>
                                )}
                            </>
                        )}
                    </OverlayWidth>
                </rModal.Overlay>
            </rModal.Portal>
        </rModal.Root>
    );
};
